import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Manager, Reference, Popper } from 'react-popper';
import defaultsDeep from 'lodash/defaultsDeep';
import noop from 'lodash/noop';
import { Button, Icon, useSparkConfig, buttonTypes } from 'xm-spark';

import style from './Dropdown.module.scss';

const DEFAULT_POPPER_PROPS = {
  modifiers: {
    preventOverflow: {
      escapeWithReference: true,
    },
  },
};

const getPopperProps = popperProps =>
  defaultsDeep(popperProps, DEFAULT_POPPER_PROPS);

/**
 * Dropdown provides a Button dropdown menu with customizable content.
 * Use with `DropdownItem` components as children to get a fully-featured dropdown menu.
 */
export default function Dropdown({
  simple,
  children,
  className,
  classes,
  disabled,
  icon,
  label,
  placement,
  popperProps,
  portalNode,
  testId,
  onOutOfBoundaries,
  buttonProps,
  onClose,
  onOpen,
  focusIndex,
  setFocusIndex,
  showCount,
  itemCount,
  ...otherProps
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef();
  const menuRef = useRef();

  const closeDropdown = useCallback(
    e => {
      // when user clicks on the button itself
      if (dropdownRef.current?.contains(e.target)) {
        return;
      }
      setIsOpen(false);
      if (onClose) {
        onClose(e);
      }
    },
    [onClose, dropdownRef],
  );

  useEffect(() => {
    window.addEventListener('click', closeDropdown);
    return () => {
      window.removeEventListener('click', closeDropdown);
    };
  }, [closeDropdown]);

  useEffect(() => {
    if (focusIndex === null) {
      setIsOpen(false);
    }
  }, [focusIndex]);

  const handleClickDropdown = e => {
    if (disabled) {
      return;
    }
    setIsOpen(!isOpen);
    if (isOpen && onClose) {
      onClose(e);
    } else if (!isOpen && onOpen) {
      onOpen(e);
    }
  };

  const handleKeyUp = e => {
    if (e.key === ' ' || e.key === 'Enter') {
      setFocusIndex(0);
    }
  };

  const { getPopoverContainer } = useSparkConfig();

  return (
    <Manager tag={false}>
      <Reference>
        {({ ref }) => (
          <div
            ref={ref}
            className={classnames(className, style.dropdown, {
              visible: isOpen,
              [style.active]: isOpen,
            })}
            {...(testId ? { 'data-testid': testId } : {})}
            {...otherProps}
          >
            <div ref={dropdownRef}>
              {simple === true ? (
                <div
                  role="button"
                  tabIndex={0}
                  aria-haspopup
                  aria-expanded={isOpen}
                  onClick={handleClickDropdown}
                  onKeyUp={e => {
                    handleKeyUp(e);
                    // divs won't fire click handlers on Space/Enter automatically
                    if (e.key === ' ' || e.key === 'Enter') {
                      handleClickDropdown();
                    }
                  }}
                  {...(testId
                    ? { 'data-testid': `${testId}Btn` }
                    : { 'data-testid': 'dropdownExpandBtn' })}
                  {...buttonProps}
                >
                  {label}
                </div>
              ) : (
                <Button
                  disabled={disabled}
                  selected={isOpen}
                  aria-haspopup
                  aria-expanded={isOpen}
                  onClick={handleClickDropdown}
                  onKeyUp={handleKeyUp}
                  {...(testId
                    ? { testId: `${testId}Btn` }
                    : { testId: 'dropdownExpandBtn' })}
                  {...buttonProps}
                >
                  {icon && <Icon type={icon} />}
                  <span>{label}</span>
                  {showCount && itemCount > 1 && (
                    <span className={classnames(style.badge, classes.badge)}>
                      {itemCount}
                    </span>
                  )}
                  <Icon type="icon-caret-down" />
                </Button>
              )}
            </div>
          </div>
        )}
      </Reference>
      {isOpen &&
        ReactDOM.createPortal(
          <Popper placement={placement} {...getPopperProps(popperProps)}>
            {({
              ref,
              outOfBoundaries,
              style: popperStyle,
              placement: popperPlacement,
            }) => {
              if (outOfBoundaries && onOutOfBoundaries()) {
                setIsOpen(false);
                return null;
              }
              return (
                <div
                  ref={ref}
                  style={popperStyle}
                  data-placement={popperPlacement}
                  data-out-of-boundaries={outOfBoundaries}
                  {...(testId ? { 'data-testid': `${testId}-popup` } : {})}
                >
                  <div
                    style={{
                      minWidth: dropdownRef.current?.offsetWidth ?? 0,
                    }}
                    className={classnames(style.menu, classes.menu)}
                  >
                    <ul ref={menuRef} role="menu" className={style.list}>
                      {children}
                    </ul>
                  </div>
                </div>
              );
            }}
          </Popper>,
          portalNode || getPopoverContainer(null),
        )}
    </Manager>
  );
}

Dropdown.displayName = 'Dropdown';

Dropdown.propTypes = {
  /**
   * Simple prop will remove the button / icon, leaving the entire label up to you.
   */
  simple: PropTypes.bool,
  /**
   * node to put in as the label for the menu button
   */
  label: PropTypes.node,
  /**
   * `Icon.type` string, see: [Icon](#icon)
   */
  icon: PropTypes.node,
  /**
   * class to apply to the dropdown
   */
  className: PropTypes.string,
  /**
   * classNames to add to inner components
   */
  classes: PropTypes.shape({
    badge: PropTypes.string,
    menu: PropTypes.string,
  }),
  /**
   * disabled state, passed straight to underlying menu button
   */
  disabled: PropTypes.bool,
  /**
   * component children
   */
  children: PropTypes.node,
  /**
   * `placement` value for popper.js see [reference](https://popper.js.org/popper-documentation.html#Popper.placements)
   */
  placement: PropTypes.string,
  /**
   * Any other popper props that are spread onto <Popper />
   */
  popperProps: PropTypes.shape({}),
  /**
   * parent of popper
   */
  portalNode: PropTypes.shape(),
  /**
   * onOutOfBoundaries: function run when out of popper boundariesElement
   * If return value of method is truthy, dropdown will automatically close
   */
  onOutOfBoundaries: PropTypes.func,
  /**
   * `data-testid` value to set on the clickable "button" part of the component.
   * The popup part of the component will also have a `data-testid` set on it which is obtained by adding the `-popup`
   * suffix to the value.
   */
  testId: PropTypes.string,
  /**
   * Callback function to be executed when the Dropdown closes
   */
  onClose: PropTypes.func,
  /**
   * Callback function to be executed when the Dropdown opens
   */
  onOpen: PropTypes.func,
  /**
   * Props to be spread onto the Button
   */
  buttonProps: PropTypes.shape(buttonTypes),
  /**
   * Index of dropdown list item with focus.
   */
  focusIndex: PropTypes.number,
  /**
   * Callback to update the current focus index.
   */
  setFocusIndex: PropTypes.func,

  /**
   * Props to display number of items selected
   */
  itemCount: PropTypes.number,

  /**
   * Props to indicate if we show the itemCount or not
   */
  showCount: PropTypes.bool,
};

Dropdown.defaultProps = {
  simple: false,
  icon: null,
  label: null,
  className: '',
  classes: {},
  disabled: false,
  children: null,
  placement: 'bottom',
  popperProps: {
    modifiers: {
      preventOverflow: {
        boundariesElement: 'window',
      },
    },
  },
  portalNode: null,
  onOutOfBoundaries: () => true,
  testId: null,
  onClose: noop,
  onOpen: noop,
  buttonProps: {},
  focusIndex: null,
  setFocusIndex: noop,
  showCount: false,
  itemCount: 0,
};
